import { useQueryClient } from '@tanstack/react-query'
import {
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ROUTES } from '@/constants/routes'
import { consoleCopy, overlayCopy } from '@/copy'
import { Alert } from '@/ui/Alert/Alert'
import { AvatarGroup } from '@/ui/AvatarGroup/AvatarGroup'
import { Button } from '@/ui/Button/Button'
import { Card } from '@/ui/Card/Card'
import { Chip } from '@/ui/Chip/Chip'
import { Disclosure } from '@/ui/Disclosure/Disclosure'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { CheckboxField } from '@/ui/Field/Field'
import { Skeleton } from '@/ui/Loading/Loading'
import { useSkeletonDelay } from '@/ui/Loading/useSkeletonDelay'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { ConfirmDialog } from '@/ui/Modal/Modal'
import { hasOpenLayer } from '@/ui/overlay/escapeStack'
import { SearchInput } from '@/ui/SearchInput/SearchInput'
import { SectionHeader } from '@/ui/SectionHeader/SectionHeader'
import { lifecycleTone } from '@/ui/Status/lifecycle'
import { StatusBadge, StatusDot } from '@/ui/Status/Status'
import { messagesApi } from '../../api/messages'
import { useThreadsQuery } from '../../hooks/queries'
import { useNow } from '../../hooks/useNow'
import { useThreadSelection } from '../../hooks/useThreadSelection'
import { streamKey, useStreamStore } from '../../store/stream'
import { useToastStore } from '../../store/toast'
import type { AgentParticipant, ThreadSummary } from '../../types/api'
import { RefreshFailed } from '../RefreshFailed'
import styles from './ThreadBoard.module.css'
import { resolveParticipants, threadAgeDisplay, threadBucket } from './tags'

type Filter = 'all' | 'needs_you' | 'active' | 'resolved' | 'mine'

type Bucket = 'needs_you' | 'active' | 'resolved'

const FILTERS: Filter[] = ['all', 'needs_you', 'active', 'resolved', 'mine']

interface Props {
  topicId: string
  agents: AgentParticipant[]
  currentUser: string | null
  onSelect: (threadId: string) => void
}

/**
 * The redesigned threads board (§3): status-grouped cards under
 * NEEDS YOU / ACTIVE / RESOLVED with instant client-side filter chips.
 * Reads the active bucket only (archived/trash stay on the old pane
 * until the Phase 6 cutover decides their final home).
 */
export function ThreadBoard({ topicId, agents, currentUser, onSelect }: Props) {
  const [filter, setFilter] = useState<Filter>('all')
  const { data, isLoading, isError, refetch } = useThreadsQuery(
    topicId,
    'active',
  )
  const threads = useMemo(() => data?.threads ?? [], [data])
  const activeTurns = useStreamStore((s) => s.activeTurns)
  const now = useNow()
  const { data: archivedData } = useThreadsQuery(topicId, 'archived')
  const archived = useMemo(() => archivedData?.threads ?? [], [archivedData])
  const selection = useThreadSelection()
  const addToast = useToastStore((s) => s.addToast)
  const qc = useQueryClient()
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false)
  // Captured when the dialog opens: the selection clears before it closes.
  const [bulkDeleteCount, setBulkDeleteCount] = useState(0)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkArchiving, setBulkArchiving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  const selectAllId = useId()
  const showSkeleton = useSkeletonDelay(isLoading)

  async function bulkArchive() {
    const ids = [...selection.picked]
    if (ids.length === 0) return
    setBulkArchiving(true)
    try {
      const res = await messagesApi.archiveThreads(topicId, ids)
      void qc.invalidateQueries({ queryKey: ['threads', topicId] })
      selection.clearSelection()
      addToast(consoleCopy.board.archived(res.thread_ids.length), 'success', {
        label: consoleCopy.board.undo,
        onClick: () => {
          void messagesApi
            .unarchiveThreads(topicId, res.thread_ids)
            .then(() =>
              qc.invalidateQueries({ queryKey: ['threads', topicId] }),
            )
            .catch((err) =>
              addToast(
                err instanceof Error
                  ? err.message
                  : consoleCopy.board.undoFailed,
                'error',
              ),
            )
        },
      })
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : consoleCopy.board.archiveFailed(ids.length),
        'error',
      )
    } finally {
      setBulkArchiving(false)
    }
  }

  async function bulkDelete() {
    const ids = [...selection.picked]
    if (ids.length === 0) return
    setBulkDeleting(true)
    try {
      const res = await messagesApi.purgeThreads(topicId, ids)
      void qc.invalidateQueries({ queryKey: ['threads', topicId] })
      selection.clearSelection()
      setConfirmBulkDelete(false)
      addToast(consoleCopy.board.deleted(res.thread_ids.length), 'success')
    } catch (err) {
      addToast(
        err instanceof Error
          ? err.message
          : consoleCopy.board.deleteFailed(ids.length),
        'error',
      )
    } finally {
      setBulkDeleting(false)
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (
        e.key === '/' &&
        !e.metaKey &&
        !e.ctrlKey &&
        !(e.target instanceof HTMLInputElement) &&
        !(e.target instanceof HTMLTextAreaElement) &&
        !hasOpenLayer()
      ) {
        e.preventDefault()
        searchRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    return threads.filter((t) => {
      if (filter !== 'all') {
        if (filter === 'mine') {
          if (
            !t.participants.some(
              (p) => p.kind === 'user' && p.display_id === currentUser,
            )
          )
            return false
        } else if (threadBucket(t) !== filter) {
          return false
        }
      }
      if (q) {
        const title = (t.title ?? '').toLowerCase()
        const preview = (t.root_preview ?? '').toLowerCase()
        if (!title.includes(q) && !preview.includes(q)) return false
      }
      return true
    })
  }, [threads, filter, currentUser, searchQuery])

  const groups: { key: Bucket; label: string; items: ThreadSummary[] }[] = (
    ['needs_you', 'active', 'resolved'] as const
  ).map((key) => ({
    key,
    label: consoleCopy.board.filters[key],
    items: filtered
      .filter((t) => threadBucket(t) === key)
      .sort((a, b) => b.last_active_at - a.last_active_at),
  }))

  // The count line speaks only while a chip or the keyword narrows the board.
  const filtering =
    !isLoading && (filter !== 'all' || searchQuery.trim() !== '')

  const allPicked =
    filtered.length > 0 &&
    filtered.every((t) => selection.isPicked(t.thread_id))
  const somePicked =
    filtered.length > 0 && selection.picked.size > 0 && !allPicked

  let body: ReactNode
  if (isLoading) {
    body = showSkeleton ? (
      <Stack gap="space-8">
        {[0, 1, 2].map((i) => (
          <Card key={i}>
            <Skeleton lines={2} ground="surface" />
          </Card>
        ))}
      </Stack>
    ) : null
  } else if (isError && !data) {
    body = (
      <Alert tone="danger">
        <Inline gap="space-8" align="baseline">
          {consoleCopy.board.loadFailed}
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            {consoleCopy.retry}
          </Button>
        </Inline>
      </Alert>
    )
  } else if (groups.every((g) => g.items.length === 0)) {
    body =
      threads.length === 0 ? (
        <EmptyState
          title={consoleCopy.board.empty}
          titleAs="h2"
          description={consoleCopy.board.emptyHint}
        />
      ) : (
        <EmptyState
          variant="inline"
          description={consoleCopy.board.noMatch(filter, searchQuery.trim())}
          action={
            <Button
              variant="outline"
              onClick={() => {
                setFilter('all')
                setSearchQuery('')
              }}
            >
              {consoleCopy.board.clearFilters}
            </Button>
          }
        />
      )
  } else {
    body = (
      <Stack gap="space-24">
        {groups.map(
          (g) =>
            g.items.length > 0 && (
              <section key={g.key}>
                <SectionHeader
                  as="h2"
                  trailingSpacing="section"
                  count={g.items.length}
                >
                  {g.label}
                </SectionHeader>
                <Stack gap="space-12">
                  {g.items.map((t) => (
                    <ThreadCard
                      key={t.thread_id}
                      topicId={topicId}
                      thread={t}
                      bucket={g.key}
                      agents={agents}
                      live={
                        (activeTurns[streamKey(topicId, t.thread_id)] ?? [])
                          .length > 0
                      }
                      now={now}
                      onSelect={onSelect}
                      selectMode={selection.selectMode}
                      picked={selection.isPicked(t.thread_id)}
                      onTogglePick={() => selection.togglePick(t.thread_id)}
                    />
                  ))}
                </Stack>
              </section>
            ),
        )}
      </Stack>
    )
  }

  return (
    <div className={styles.board}>
      {/* Controls align on their tops, not on the column's centre: the search
          field's count hangs below it and must not lift the field (grid-and-alignment). */}
      <Inline gap="space-8" align="start" className={styles.toolbar}>
        <Inline
          gap="space-6"
          role="group"
          aria-label={consoleCopy.board.filterGroup}
        >
          {FILTERS.map((f) => (
            <Chip
              key={f}
              variant="filter"
              size="default"
              label={consoleCopy.board.filters[f]}
              pressed={filter === f}
              onPressedChange={() => setFilter(f)}
            />
          ))}
        </Inline>
        <Inline
          gap="space-8"
          align="start"
          wrap={false}
          className={styles.tools}
        >
          <SearchInput
            ref={searchRef}
            className={styles.search}
            label={consoleCopy.board.searchLabel}
            placeholder={consoleCopy.board.searchPlaceholder}
            value={searchQuery}
            onValue={setSearchQuery}
            resultCount={filtering ? filtered.length : undefined}
            totalCount={filtering ? threads.length : undefined}
            countNoun={consoleCopy.board.countNoun}
          />
          <Button
            variant="outline"
            active={selection.selectMode}
            onClick={selection.toggleSelectMode}
          >
            {consoleCopy.board.select}
          </Button>
        </Inline>
      </Inline>

      {selection.selectMode && (
        <Inline gap="space-8" className={styles.bulk}>
          <CheckboxField
            id={selectAllId}
            label={
              selection.picked.size > 0
                ? consoleCopy.board.selected(selection.picked.size)
                : consoleCopy.board.selectAll
            }
            checked={allPicked}
            indeterminate={somePicked}
            onChecked={() =>
              selection.selectAll(filtered.map((t) => t.thread_id))
            }
          />
          <Button
            variant="outline"
            disabled={selection.picked.size === 0}
            loading={bulkArchiving}
            onClick={() => void bulkArchive()}
          >
            {consoleCopy.board.archive}
          </Button>
          <Button
            variant="outline"
            disabled={selection.picked.size === 0}
            onClick={() => {
              setBulkDeleteCount(selection.picked.size)
              setConfirmBulkDelete(true)
            }}
          >
            {consoleCopy.board.delete}
          </Button>
          <Button variant="outline" onClick={selection.clearSelection}>
            {overlayCopy.cancel}
          </Button>
        </Inline>
      )}

      <div className={styles.body} aria-busy={isLoading || undefined}>
        {isError && data && (
          <RefreshFailed
            message={consoleCopy.board.loadFailed}
            onRetry={() => void refetch()}
          />
        )}
        {body}
      </div>
      <ArchivedSection topicId={topicId} threads={archived} />
      <ConfirmDialog
        open={confirmBulkDelete}
        title={consoleCopy.board.confirmDelete.title(bulkDeleteCount)}
        tone="danger"
        description={consoleCopy.board.confirmDelete.description(
          bulkDeleteCount,
        )}
        confirmLabel={consoleCopy.board.confirmDelete.confirm}
        onConfirm={() => void bulkDelete()}
        onClose={() => setConfirmBulkDelete(false)}
        loading={bulkDeleting}
      />
    </div>
  )
}

/**
 * Collapsed-by-default home for archived threads (Phase 6: replaced the
 * old pane's view tabs + bulk actions): per-row restore or delete
 * forever. Trashed threads are not listed — the server's retention
 * sweeper purges them automatically.
 */
function ArchivedSection({
  topicId,
  threads,
}: {
  topicId: string
  threads: ThreadSummary[]
}) {
  const [confirmId, setConfirmId] = useState<string | null>(null)
  // Which action is pending: a row's restore, or the confirmed purge.
  const [busy, setBusy] = useState<string | null>(null)
  const qc = useQueryClient()
  const addToast = useToastStore((s) => s.addToast)

  if (threads.length === 0) return null

  async function run(
    key: string,
    fn: () => Promise<unknown>,
    ok: string,
    failed: string,
  ) {
    setBusy(key)
    try {
      await fn()
      void qc.invalidateQueries({ queryKey: ['threads', topicId] })
      addToast(ok, 'success')
      setConfirmId(null)
    } catch (err) {
      addToast(err instanceof Error ? err.message : failed, 'error')
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <Disclosure
        label={consoleCopy.board.archivedSection}
        count={threads.length}
      >
        <Stack gap="space-12">
          {threads.map((t) => (
            <Inline
              key={t.thread_id}
              justify="between"
              gap="space-8"
              wrap={false}
            >
              <Text className={styles.grow}>
                {t.title || t.root_preview || consoleCopy.board.untitled}
              </Text>
              <Inline gap="space-6" wrap={false}>
                <Button
                  variant="outline"
                  loading={busy === `restore:${t.thread_id}`}
                  onClick={() =>
                    void run(
                      `restore:${t.thread_id}`,
                      () =>
                        messagesApi.unarchiveThreads(topicId, [t.thread_id]),
                      consoleCopy.board.restored,
                      consoleCopy.board.restoreFailed,
                    )
                  }
                >
                  {consoleCopy.board.restore}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setConfirmId(t.thread_id)}
                >
                  {consoleCopy.board.deleteForever}
                </Button>
              </Inline>
            </Inline>
          ))}
        </Stack>
      </Disclosure>
      <ConfirmDialog
        open={!!confirmId}
        title={consoleCopy.board.confirmPurge.title}
        tone="danger"
        description={consoleCopy.board.confirmPurge.description}
        confirmLabel={consoleCopy.board.confirmDelete.confirm}
        onConfirm={() =>
          confirmId &&
          void run(
            'purge',
            () => messagesApi.purgeThreads(topicId, [confirmId]),
            consoleCopy.board.purged,
            consoleCopy.board.purgeFailed,
          )
        }
        onClose={() => setConfirmId(null)}
        loading={busy === 'purge'}
      />
    </>
  )
}

function ThreadCard({
  topicId,
  thread,
  bucket,
  agents,
  live,
  now,
  onSelect,
  selectMode,
  picked,
  onTogglePick,
}: {
  topicId: string
  thread: ThreadSummary
  bucket: Bucket
  agents: AgentParticipant[]
  live: boolean
  now: number
  onSelect: (id: string) => void
  selectMode?: boolean
  picked?: boolean
  onTogglePick?: () => void
}) {
  const pickId = useId()
  const people = resolveParticipants(thread.participants, agents)
  const meta = [
    people.length > 0
      ? people
          .slice(0, 3)
          .map((p) => p.name)
          .join(', ') +
        (people.length > 3
          ? ` ${consoleCopy.board.moreParticipants(people.length - 3)}`
          : '')
      : null,
    consoleCopy.board.replies(thread.reply_count),
    threadAgeDisplay(thread.last_active_at, now),
  ]
    .filter(Boolean)
    .join(' · ')
  const title =
    thread.title || thread.root_preview || consoleCopy.board.untitled
  const badge = bucket === 'needs_you' ? 'needs you' : bucket

  const content = (
    <>
      <Inline gap="space-8" align="start" wrap={false}>
        {selectMode ? (
          <div className={styles.grow}>
            <CheckboxField
              id={pickId}
              ground="surface"
              label={
                <Text weight="strong" tone="text">
                  {title}
                </Text>
              }
              checked={!!picked}
              onChecked={() => onTogglePick?.()}
              inputProps={{
                'aria-label': consoleCopy.board.selectThread(title),
              }}
            />
          </div>
        ) : (
          <Text weight="strong" tone="text" className={styles.grow}>
            {title}
          </Text>
        )}
        {live && (
          <Text variant="caption" tone="text-sub">
            <StatusDot
              tone={lifecycleTone('live')}
              label={consoleCopy.board.live}
            />
          </Text>
        )}
        <StatusBadge tone={lifecycleTone(badge)}>
          {consoleCopy.threadStatus[bucket]}
        </StatusBadge>
      </Inline>
      <Inline gap="space-8" wrap={false}>
        <Text
          variant="caption"
          tone="text-mute"
          numeric
          className={styles.grow}
        >
          {meta}
        </Text>
        {people.length > 0 && (
          <AvatarGroup
            ground="surface"
            participants={people.map((p) => ({
              name: p.name,
              kind: p.isAgent ? 'agent' : 'human',
            }))}
          />
        )}
      </Inline>
    </>
  )

  // D92: in select mode the card is a plain div and its checkbox, labelled by
  // the title, is the one target (Card: a card holding a control is not
  // clickable itself).
  if (selectMode) return <Card>{content}</Card>
  return (
    <Card
      as="a"
      href={ROUTES.topicThread(topicId, thread.thread_id)}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)
          return
        e.preventDefault()
        onSelect(thread.thread_id)
      }}
    >
      {content}
    </Card>
  )
}
